import React, { useState, useEffect } from 'react';
import { Layout } from 'antd';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
// import CryptoHelper from 'utils/cryptHelper';
import { UserAccessContext } from 'context/userAccess';
import { ACCESS_MATRIX } from 'global_config/constant-global';
import { getUserAccess } from 'utils/user';
import { decryptJSON, encryptJSON } from 'services/login/login';
import { getSessionUserData, sendAuditTrailLog } from 'services/system_setup/auditTrail';
import Header from './header';
import Content from './content';
import Sider from './sider';

import './layout.css';
import { fetchAvailableDrList } from 'services/delivery_receipts/delivery_receipts';
import { invPrintexTrxnList } from 'services/invoice/invoice';
// import { user } from 'services/login/login';

const history = createBrowserHistory({ basename: process.env.PUBLIC_URL });

const initialAccessState = {
	view: false,
	create: false,
	update: false,
	print: false,
	// post: false,
	cancel: false, 
};

const ROUTE_MODULE_CODE_MAP = {
	home: 'hm',
	user: 'usr',
	agent: 'agnt',
	customer: 'cust',
	invoice: 'inv',
	delivery: 'dr',
	payment: 'pymt',
	setup_maintenance: 'sys',
	system_setup: 'sys',
	lookuptable: 'lkp',
	accounting_reports: 'rpt',
	dr_reports: 'RPT_DR',
};

function buildRouteAuditLogPayload(pathname = '', action = 'NAVIGATE') {
	const segments = String(pathname || '')
		.split('/')
		.filter(Boolean)
		.map(segment => segment.toLowerCase());
	const moduleSegment = segments[0] || '';
	const subModuleSegment = segments[1] || '';
	const moduleCode = ROUTE_MODULE_CODE_MAP[moduleSegment] || 'sys';
	const activitySuffix = (subModuleSegment || moduleSegment || 'unknown')
		.replace(/[^a-z0-9_]/gi, '_')
		.toUpperCase();
	const activityCode = `ROUTE_${String(action || 'NAV').toUpperCase()}_${moduleCode}_${activitySuffix}`.slice(0, 50);
	const parameters = JSON.stringify({
		routePath: pathname,
		routeAction: action,
	});

	return {
		module_code: moduleCode,
		sub_module_code: subModuleSegment || null,
		activity_code: activityCode,
		is_success: 1,
		description: `Route ${String(action || 'NAVIGATE').toUpperCase()} ${pathname}`.slice(0, 3900),
		parameters: parameters.length > 3900 ? `${parameters.slice(0, 3900)}...` : parameters,
	};
}

const MainLayout = () => {
	const [userAccess, setUserAccess] = useState({
		agents: initialAccessState,
		customersSetup: initialAccessState,
		deliveryReceipt: initialAccessState,
		invoices: initialAccessState,
		payments: initialAccessState,
		home: initialAccessState,
		systemSetup: initialAccessState,
		users: initialAccessState,
		lkpSetup: initialAccessState,
		reports: initialAccessState,
		RPT_DR: initialAccessState,
		settings: initialAccessState,
	});

	const [freeDrNos, setFreeDrNos] = useState(null);
	const [printedInvList, setPrintedInvList] = useState([]);

	function parseAccessMatrixFromStorage(cipherOrJson) {
		if (!cipherOrJson) return null;
		// 1) Try plain JSON first (covers legacy/plain-storage)
		try {
			const parsed = JSON.parse(cipherOrJson);
			// Normalize: store back as encrypted for future reads
			try { sessionStorage.setItem(ACCESS_MATRIX, encryptJSON(parsed)); } catch (_) {}
			return parsed;
		} catch (_) {
			// 2) Try decrypt (current format)
			try {
				return decryptJSON(cipherOrJson);
			} catch (_) {
				return null;
			}
		}
	}

	function defineUserAccess({ accessMatrix }) {
		const agents = accessMatrix.find(item => item.moduleCode === "agnt");
		const customersSetup = accessMatrix.find(item => item.moduleCode === "cust");
		const deliveryReceipt = accessMatrix.find(item => item.moduleCode === "dr");
		const systemSetup = accessMatrix.find(item => item.moduleCode === "sys");
		const invoices = accessMatrix.find(item => item.moduleCode === "inv");
		const lkpSetup = accessMatrix.find(item => item.moduleCode === "lkp");
		const users = accessMatrix.find(item => item.moduleCode === "usr");
		const payments = accessMatrix.find(item => item.moduleCode === "pymt");
		const home =  accessMatrix.find(item => item.moduleCode === "hm");
		const reports =  accessMatrix.find(item => item.moduleCode === "rpt");
		const drReports =  accessMatrix.find(item => item.moduleCode === "RPT_DR");
		const settings = { vw: 'TRUE', crt: 'TRUE', edt: 'TRUE', pst: 'TRUE', cncl: 'TRUE', prnt: 'TRUE' };

		setUserAccess({
			agents: getUserAccess(agents),
			customersSetup: getUserAccess(customersSetup),
			deliveryReceipt: getUserAccess(deliveryReceipt),
			systemSetup: getUserAccess(systemSetup),
			invoices:  getUserAccess(invoices),
			users: getUserAccess(users),
			lkpSetup: getUserAccess(lkpSetup),
			payments: getUserAccess(payments),
			home: getUserAccess(home),
			reports: getUserAccess(reports),
			RPT_DR: getUserAccess(drReports),
			settings: getUserAccess(settings),
		});
	}

	const getFreeDrNos = async () =>{
		const freeDrNos = await fetchAvailableDrList();
		// console.log('test =>', data);
		setFreeDrNos(freeDrNos);
	}

	const getPrintedTrxnList = async () =>{
		const data = await invPrintexTrxnList();
		setPrintedInvList(data);
	}

	useEffect(() => {
		const stringAccMatrix = sessionStorage.getItem(ACCESS_MATRIX);
		// console.log('stringAccMatrix =>', stringAccMatrix);
// 		console.log('CRYPTR KEY (layout) =>', process.env.REACT_APP_CRYPTR_KEY);
// console.log('Encrypted string:', stringAccMatrix);

		if (stringAccMatrix) {
			const accessMatrix = parseAccessMatrixFromStorage(stringAccMatrix);
			if (accessMatrix) {
				// console.log('decryptedMatrix =>', accessMatrix);
				defineUserAccess({ accessMatrix });
			} else {
				console.log('Error decrypting access matrix: => Could not parse stored value');
				try { sessionStorage.removeItem(ACCESS_MATRIX); } catch (_) {}
			}
		}
		
		getFreeDrNos();
		getPrintedTrxnList();

		const routeAuditUser = getSessionUserData();
		if (routeAuditUser && routeAuditUser.userID) {
			const initialRoutePayload = buildRouteAuditLogPayload(window.location.pathname, 'LOAD');
			void sendAuditTrailLog({
				...initialRoutePayload,
				created_by: routeAuditUser.userID,
			});
		}

		const unlisten = history.listen((location, action) => {
			const userData = getSessionUserData();
			if (!userData || !userData.userID) return;

			const payload = buildRouteAuditLogPayload(location.pathname, action);
			void sendAuditTrailLog({
				...payload,
				created_by: userData.userID,
			});
		});

		return () => {
			unlisten();
		};
	}, []);

	return (
		<Router history={history}>
			<UserAccessContext.Provider value={{ userAccess, defineUserAccess, freeDrNos, printedInvList }}>
				<Layout style={{ minHeight: '100vh' }}>
					<Header />
					<Layout>
						<Sider 
							// @ts-ignore
							collapsed 
						/>
						<Content />
					</Layout>
				</Layout>
			</UserAccessContext.Provider>
		</Router>
	);
}

export default MainLayout;
